import { Link, NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ScrollToTop } from '../lib/ScrollToTop.js';
import { CalendarDays, Clock3, Fingerprint, IndianRupee, UserRound } from 'lucide-react';
import { t } from '../i18n/index.js';
import { useAuth } from '../lib/auth.js';
import { firstName, greeting } from '../lib/greeting.js';
import { HealthBadge } from '../lib/HealthBadge.js';
import { Avatar, Skeleton } from '../lib/components.js';
import { photoSrc } from '../lib/PhotoPicker.js';
import { Alert } from '../lib/ui.js';
import { ThemeToggle } from '../lib/ThemeToggle.js';
import { AuthShell } from '../lib/AuthShell.js';
import { BrandMark } from '../lib/Brand.js';
import { InvitePage } from './pages/InvitePage.js';
import { LoginPage } from './pages/LoginPage.js';
import { ReregisterPage } from './pages/ReregisterPage.js';
import { ProfilePage } from './pages/ProfilePage.js';
import { PunchPage } from './pages/PunchPage.js';
import { AddToHomePage } from './pages/AddToHomePage.js';
import { GetTheAppPage } from './pages/GetTheAppPage.js';
import { PrivacyPage } from './pages/PrivacyPage.js';
import { BankDetailsPage } from './pages/BankDetailsPage.js';
import { DocumentsPage } from './pages/DocumentsPage.js';
import { OffDaysPage } from './pages/OffDaysPage.js';
import { HoursPage } from './pages/HoursPage.js';
import { UpdatePrompt } from './UpdatePrompt.js';
import { apiBaseLooksWrongForNative, API_URL } from '../lib/api.js';
import { isNativeApp } from '../lib/native.js';

export function WorkerApp() {
  const { me } = useAuth();
  const signedIn = !!me?.user && me.session?.kind === 'worker';
  const { pathname } = useLocation();
  /**
   * Paths that must render for somebody with no session, and the list that actually decides it:
   * the sign-in screen is returned below before the router is reached, so a public <Route> on
   * its own renders nothing. Adding a page here is part of making it public.
   *
   * Why each one: an invite and a re-registration link are how somebody gets an account at all;
   * the download page, Get The App and the privacy policy all have to work for a person holding
   * a wiped phone, and Google checks the privacy policy without signing in to anything.
   */
  const PUBLIC_PATHS = ['/invite/', '/reregister', '/android', '/get-the-app', '/privacy'];
  const isPublic = PUBLIC_PATHS.some((p) => pathname.startsWith(p));

  // Signed out, on a page that needs an account: the designed sign-in screen.
  if (me && !signedIn && !isPublic) {
    return (
      <AuthShell>
        <LoginPage />
      </AuthShell>
    );
  }

  return (
    <div className="shell worker">
      <header>
        <BrandMark size={30} />
        {/* Greeting first since the October 2026 look, the app's name under it; signed out, the
            name alone. */}
        <div className="brand-text">
          {me?.user ? (
            <>
              <h1 className="greet-line">
                {greeting()}, <strong>{firstName(me.user.name)}</strong>
              </h1>
              <span className="greet">{t('app_name')}</span>
            </>
          ) : (
            <h1>{t('app_name')}</h1>
          )}
        </div>
        <HealthBadge />
        <ThemeToggle />
        {/* Their own face, which is also the way to their profile. Named "Your Account", not
            "Profile": the tab bar already has a Profile link, and two links of one name are two
            things a screen reader cannot tell apart (a spec caught it on 14 October). */}
        {signedIn && me?.user && (
          <Link to="/profile" className="header-avatar" aria-label={t('header_account')}>
            <Avatar
              name={me.user.name}
              size={38}
              photoUrl={photoSrc('/me/photo', me.photo_updated_at)}
            />
          </Link>
        )}
      </header>
      <ScrollToTop />
      <main>
        {/* A build packaged with a relative API base cannot reach the server at all: every call
            resolves to a file inside the APK. Say so once, plainly, instead of letting each
            screen fail on its own with whatever the parser made of index.html. */}
        {isNativeApp() && apiBaseLooksWrongForNative() && (
          <div className="card">
            <Alert>{t('app_misconfigured', { url: API_URL })}</Alert>
          </div>
        )}
        {/* Nothing in a browser; in the app, the only thing that says a build is out of date. */}
        <UpdatePrompt />
        {!me ? (
          <Skeleton lines={4} />
        ) : (
          <Routes>
            {/* Public: somebody whose build is too old is often signed out, and a new worker has
                no account at all until they open their invite. */}
            {/* The old APK download page. Google Play is the only way in (29 October 2026): Play
                suspended the app for leading to an APK, and the listing links to this site. An
                old link lands on Get The App, which offers Play only; inside the app, home. */}
            <Route
              path="/android"
              element={<Navigate to={isNativeApp() ? '/' : '/get-the-app'} replace />}
            />
            {/* Public: the address an admin re-sends to somebody who lost their phone, and the
                policy Google Play requires anybody to be able to open. */}
            <Route path="/get-the-app" element={<GetTheAppPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/invite/:token" element={<InvitePage />} />
            <Route path="/reregister" element={<ReregisterPage />} />
            {signedIn ? (
              <>
                <Route path="/" element={<PunchPage />} />
                <Route path="/add-to-home" element={<AddToHomePage />} />
                {/* Asked once, straight after the passkey; reachable for ever after from Profile. */}
                <Route path="/bank-details" element={<BankDetailsPage />} />
                <Route path="/profile/bank" element={<BankDetailsPage mode="edit" />} />
                <Route path="/hours" element={<HoursPage />} />
                <Route path="/off-days" element={<OffDaysPage />} />
                <Route path="/documents" element={<DocumentsPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            ) : (
              <Route path="*" element={<LoginPage />} />
            )}
          </Routes>
        )}
      </main>
      {signedIn && (
        <nav className="tabbar" aria-label="Main">
          <NavLink to="/" end>
            <Fingerprint size={22} aria-hidden="true" />
            {t('nav_punch')}
          </NavLink>
          <NavLink to="/hours">
            <Clock3 size={22} aria-hidden="true" />
            {t('nav_hours')}
          </NavLink>
          <NavLink to="/off-days">
            <CalendarDays size={22} aria-hidden="true" />
            {t('nav_off_days')}
          </NavLink>
          <NavLink to="/documents">
            <IndianRupee size={22} aria-hidden="true" />
            {t('nav_documents')}
          </NavLink>
          <NavLink to="/profile">
            <UserRound size={22} aria-hidden="true" />
            {t('nav_profile')}
          </NavLink>
        </nav>
      )}
    </div>
  );
}
